interface FormatNumberOptions {
  decimalsSeparator?: string;
  hideDecimals?: boolean;
  roundDecimals?: boolean;
  tousandSeparator?: string;
}

/**
 * Formats a number or a string representing a number by adding a dot as the thousands separator
 * and a comma for decimals. Provides options to either hide decimals or round to two decimal places.
 *
 * @param {number|string} value - The value to format, can be either a number or a string representing a number.
 * @param {FormatNumberOptions} options - An options object for formatting:
 *   - `hideDecimals` (optional): If true, hides decimal places by rounding to the nearest whole number.
 *   - `roundDecimals` (optional): If true, rounds the number to two decimal places.
 *   - `tousandSeparator` (optional): If setted, customize the thousands separator.
 *   - `decimalsSeparator` (optional): If setted, customize the decimals separator.
 * @returns {string} The formatted number as a string.
 */

export function formatNumber(
  value: number | string,
  options: FormatNumberOptions = {}
): string {
  const {
    decimalsSeparator = ',',
    hideDecimals = false,
    roundDecimals = false,
    tousandSeparator = '.'
  } = options;
  let number = typeof value === 'string' ? parseFloat(value) : value;

  if (hideDecimals) {
    number = Math.round(number);
  } else if (roundDecimals) {
    number = Math.round(number * 100) / 100;
  }

  const parts = number.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, tousandSeparator);

  if (parts.length > 1) {
    parts[1] = parts[1].length >= 2 ? parts[1].substring(0, 2) : `${parts[1]}0`;
    return parts.join(decimalsSeparator);
  }
  return parts[0];
}
